import React from "react";
import { withInstallerAuthentication } from "../withInstallerAutentication";
import { Attachment, Job } from "../../../models/Job";
import { withParams } from "../../../hoc/navigation/withParams";
import { jobService } from "../../../services/JobService";
import { Button, CircularProgress, Container, Stack, Typography } from "@mui/material";
import CustomSnackbar from "../../../components/SnackbarDialog";
import { MediaUploadSection } from "../../../components/attachments/FileUploader";
import { getUserId } from "../../../utils/Helpers";
import { FileUploadStatus, FileUploadStatusWidget } from "../../../components/attachments/FileUploadStatus";
import { isEmpty } from "lodash";
import { FileList } from "../../../components/attachments/FileList";
import { NoteData, NoteType } from "../../../models/NoteData";
import { InstallerJobNotes } from "./components/InstallerJobNotes";
import { CheckCircleOutline } from "@mui/icons-material";
import { InstallerJobQuestionnaire, InstallerQuestions } from "../../../models/InstallerJobQuestionnaire";
import { InstallerQuestion } from "./components/InstallerQuestion";

interface IOwnState {
    job?: Job;
    alertContent?: {
        title: string;
        message: string;
        severity: "success" | "info" | "warning" | "error";
    };
    isLoading: boolean;
    onArriveAttachments: Attachment[];
    onArriveUploadProgresses: FileUploadStatus[];
    onFinishedAttachments: Attachment[];
    onFinishedUploadProgresses: FileUploadStatus[];
    notes: NoteData[];
    didSumbitSuccessfully: boolean;
    questions: InstallerJobQuestionnaire[];
}

interface IOwnProps {
    jobId: string;
    isNonInstaller?: boolean;
}
type Props = IOwnProps & {
    [key: string]: unknown | undefined;
};

export class InstallerPageComponent extends React.PureComponent<Props, IOwnState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            job: undefined,
            alertContent: undefined,
            isLoading: false,
            onArriveAttachments: [],
            onArriveUploadProgresses: [],
            onFinishedAttachments: [],
            onFinishedUploadProgresses: [],
            notes: [],
            didSumbitSuccessfully: false,
            questions: InstallerQuestions,
        };
    }

    public componentDidMount() {
        const { jobId } = this.props;
        this.setState({ isLoading: true });
        jobService
            .fetchJobByIdAsync(jobId)
            .then((job) => {
                this.setState({
                    job,
                    didSumbitSuccessfully: this.props.isNonInstaller ? false : job?.installer_allowed_to_see === true,
                });
            })
            .catch((error) => {
                this.setState({
                    alertContent: {
                        title: "Error",
                        message: error.message,
                        severity: "error",
                    },
                });
            })
            .finally(() => {
                this.setState({ isLoading: false });
            });
        jobService.getJobAttachments(jobId).then((attachments) => {
            const onArriveAttachments = attachments.filter((attachment) => attachment.job_stage === "in-progress");
            const onFinishedAttachments = attachments.filter((attachment) => attachment.job_stage === "completed");
            this.setState({ onArriveAttachments, onFinishedAttachments });
        });
        jobService.getQuestionnaire(jobId).then((questions) => {
            if (isEmpty(questions)) {
                return;
            }
            const sortedQuestions = questions.sort((a, b) => a.order - b.order);
            // merge with default questions
            const mergedQuestions = InstallerQuestions.map((defaultQuestion) => {
                const existingQuestion = sortedQuestions.find((q) => q.question === defaultQuestion.question);
                return existingQuestion ? { ...defaultQuestion, ...existingQuestion } : defaultQuestion;
            });
            this.setState({ questions: mergedQuestions });
        });
        this.loadJobNotes();
    }

    render() {
        return (
            <Container sx={{ pl: 0 }} maxWidth={false} disableGutters>
                {this.state.alertContent ? (
                    <CustomSnackbar
                        title={this.state.alertContent.title}
                        message={this.state.alertContent.message}
                        severity={this.state.alertContent.severity}
                        isOpen={true}
                        onClose={() => this.setState({ alertContent: undefined })} // Close the snackbar
                    />
                ) : null}
                {this.state.isLoading ? (
                    <CircularProgress
                        style={{
                            position: "fixed",
                            top: "35%",
                            left: "45%",
                        }}
                    />
                ) : null}
                {!this.state.job || this.state.isLoading || this.state.didSumbitSuccessfully ? null : (
                    <Container
                        style={{
                            padding: "2em 1em",
                            background: "#F2F2F7",
                        }}>
                        <Stack direction={"row"} spacing={2} sx={{ p: 2 }}>
                            <Stack spacing={2} sx={{ width: "50%" }}>
                                <Typography variant="subtitle1">{this.state.job.description}</Typography>
                                <Typography variant="subtitle1">{this.state.job.address}</Typography>
                            </Stack>
                            <Typography
                                variant="subtitle1"
                                sx={{ width: "50%" }}
                                fontWeight={"bolder"}
                                textAlign={"end"}>
                                {this.state.job.job_order_number}
                            </Typography>
                        </Stack>
                        <hr />
                        <MediaUploadSection
                            label={
                                <>
                                    <Typography fontWeight={500} mb={2}>
                                        1- Add videos and images of the unit&nbsp;
                                        <Typography component="span" fontWeight="bolder">
                                            when you arrive:
                                        </Typography>
                                    </Typography>
                                </>
                            }
                            onFilesSelected={this.onArrivedFilesSelected}
                        />
                        {!isEmpty(this.state.onArriveUploadProgresses) ? (
                            <FileUploadStatusWidget uploads={this.state.onArriveUploadProgresses} />
                        ) : null}
                        {!isEmpty(this.state.onArriveAttachments) ? (
                            <FileList
                                attachments={this.state.onArriveAttachments}
                                onDelete={this.onDeleteAttachment}
                                label="Uploaded:"
                            />
                        ) : null}
                        {this.state.questions
                            .filter((q) => q.section === "before_installation")
                            .map((question, index) => {
                                return (
                                    <InstallerQuestion
                                        questionNumber={index + 2}
                                        key={question.id ?? index}
                                        question={question}
                                        onChange={this.onSubmitQuestion}
                                    />
                                );
                            })}
                        <InstallerJobNotes
                            notes={this.state.notes.find((note) => note.note_type === NoteType.in_progress_attachment)}
                            onNoteChange={this.saveOnArriveNotes}
                        />
                        <hr />
                        <MediaUploadSection
                            label={
                                <>
                                    <Typography fontWeight={500} mb={2}>
                                        {this.state.questions.filter((q) => q.section === "before_installation")
                                            .length + 3}
                                        - Add videos and images of the unit&nbsp;
                                        <Typography component="span" fontWeight="bolder">
                                            when you finish:
                                        </Typography>
                                    </Typography>
                                </>
                            }
                            onFilesSelected={this.onFinishedFilesSelected}
                        />
                        {!isEmpty(this.state.onFinishedUploadProgresses) ? (
                            <FileUploadStatusWidget uploads={this.state.onFinishedUploadProgresses} />
                        ) : null}
                        {!isEmpty(this.state.onFinishedAttachments) ? (
                            <FileList
                                attachments={this.state.onFinishedAttachments}
                                onDelete={this.onDeleteAttachment}
                                label="Uploaded:"
                            />
                        ) : null}
                        {this.state.questions
                            .filter((q) => q.section === "after_installation")
                            .map((question, index) => {
                                return (
                                    <InstallerQuestion
                                        questionNumber={index + 5}
                                        key={question.id ?? index}
                                        question={question}
                                        onChange={this.onSubmitQuestion}
                                    />
                                );
                            })}
                        <InstallerJobNotes
                            notes={this.state.notes.find((note) => note.note_type === NoteType.completed_attachment)}
                            onNoteChange={this.saveOnCompletedNotes}
                        />
                        <Stack mt={4} mb={4} spacing={2}>
                            <Typography fontWeight={500} mb={2}>
                                Submitting the job will send the notes and attachments to the office and close the job.
                            </Typography>
                            <Button className="basic-button" onClick={this.onSubmitJob}>
                                Submit
                            </Button>
                        </Stack>
                    </Container>
                )}
                {this.state.didSumbitSuccessfully && this.state.job ? (
                    <Container
                        style={{
                            padding: "2em 1em",
                            background: "#F2F2F7",
                        }}>
                        <Stack direction={"row"} spacing={2} sx={{ p: 2 }}>
                            <Stack spacing={2} sx={{ width: "50%" }}>
                                <Typography variant="subtitle1">{this.state.job.description}</Typography>
                                <Typography variant="subtitle1">{this.state.job.address}</Typography>
                            </Stack>
                            <Typography
                                variant="subtitle1"
                                sx={{ width: "50%" }}
                                fontWeight={"bolder"}
                                textAlign={"end"}>
                                {this.state.job.job_order_number}
                            </Typography>
                        </Stack>
                        <hr />
                        <Stack spacing={2} sx={{ p: 2 }} textAlign={"center"}>
                            <CheckCircleOutline
                                style={{
                                    alignSelf: "anchor-center",
                                    fontSize: "5rem",
                                }}
                            />
                            <Typography variant="h3" fontWeight={500}>
                                All Files Sent
                            </Typography>
                        </Stack>
                    </Container>
                ) : null}
            </Container>
        );
    }

    private onSubmitJob = () => {
        const { jobId } = this.props;
        this.setState({ isLoading: true });
        jobService
            .submitJob(jobId)
            .then(() => {
                this.setState({
                    alertContent: {
                        title: "Success",
                        message: "Job submitted successfully",
                        severity: "success",
                    },
                    didSumbitSuccessfully: true,
                });
            })
            .catch((error) => {
                this.setState({
                    alertContent: {
                        title: "Error",
                        message: `Failed to submit job: ${error.message}`,
                        severity: "error",
                    },
                    didSumbitSuccessfully: false,
                });
            })
            .finally(() => {
                this.setState({ isLoading: false });
            });
    };

    private loadJobNotes = () => {
        const { jobId } = this.props;

        jobService
            .getJobNotesAsync(jobId)
            .then((notes) => {
                this.setState({ notes });
            })
            .catch((error) => {
                this.setState({
                    alertContent: {
                        title: "Error",
                        message: error.message,
                        severity: "error",
                    },
                });
            });
    };

    private saveOnArriveNotes = (notes: NoteData) => {
        const { jobId } = this.props;
        jobService
            .upsertNoteAsync({
                ...notes,
                job_id: jobId,
                note_type: NoteType.in_progress_attachment,
            })
            .then(() => {
                this.setState({
                    alertContent: {
                        title: "Success",
                        message: "Notes saved successfully",
                        severity: "success",
                    },
                });
                this.loadJobNotes();
            })
            .catch((error) => {
                this.setState({
                    alertContent: {
                        title: "Error",
                        message: `Failed to save notes: ${error.message}`,
                        severity: "error",
                    },
                });
            });
    };

    private saveOnCompletedNotes = (notes: NoteData) => {
        const { jobId } = this.props;
        jobService
            .upsertNoteAsync({
                ...notes,
                job_id: jobId,
                note_type: NoteType.completed_attachment,
            })
            .then(() => {
                this.setState({
                    alertContent: {
                        title: "Success",
                        message: "Notes saved successfully",
                        severity: "success",
                    },
                });
                this.loadJobNotes();
            })
            .catch((error) => {
                this.setState({
                    alertContent: {
                        title: "Error",
                        message: `Failed to save notes: ${error.message}`,
                        severity: "error",
                    },
                });
            });
    };

    private onDeleteAttachment = (attachmentId: string) => {
        jobService
            .deleteJobAttachment(attachmentId)
            .then(() => {
                this.setState((prevState) => ({
                    onArriveAttachments: prevState.onArriveAttachments.filter(
                        (attachment) => attachment.id !== attachmentId,
                    ),
                    onFinishedAttachments: prevState.onFinishedAttachments.filter(
                        (attachment) => attachment.id !== attachmentId,
                    ),
                }));
            })
            .catch((error) => {
                this.setState({
                    alertContent: {
                        title: "Error",
                        message: `Failed to delete attachment: ${error.message}`,
                        severity: "error",
                    },
                });
            });
    };

    private onArrivedFilesSelected = (files: File[]) => {
        const fileUploadPromises = files.map((file) =>
            jobService.uploadJobFile(
                this.state.job?.job_id || "",
                files[0],
                "in-progress",
                this.props.isNonInstaller ? getUserId() : undefined,
                !this.props.isNonInstaller ? getUserId() : undefined,
                (progress, fileBeingUploaded) => {
                    const uploadStatus: FileUploadStatus = {
                        file: fileBeingUploaded,
                        progress,
                        status: progress === 100 ? "completed" : "uploading",
                    };
                    this.setState((prevState) => ({
                        onArriveUploadProgresses: prevState.onArriveUploadProgresses.length
                            ? prevState.onArriveUploadProgresses
                                  .map((status) =>
                                      status.file.name === fileBeingUploaded.name ? uploadStatus : status,
                                  )
                                  .filter((status) => status.progress !== 100)
                            : [uploadStatus].filter((status) => status.progress !== 100),
                    }));
                },
            ),
        );
        Promise.all(fileUploadPromises)
            .then((attachments) => {
                this.setState({
                    alertContent: {
                        title: "Success",
                        message: "File(s) uploaded successfully",
                        severity: "success",
                    },
                    onArriveAttachments: [
                        ...this.state.onArriveAttachments,
                        ...(attachments.filter((a) => a !== null) as Attachment[]),
                    ],
                });
            })
            .catch((error) => {
                this.setState({
                    alertContent: {
                        title: "Error",
                        message: error.message,
                        severity: "error",
                    },
                });
            });
    };

    private onFinishedFilesSelected = (files: File[]) => {
        const fileUploadPromises = files.map((file) =>
            jobService.uploadJobFile(
                this.state.job?.job_id || "",
                files[0],
                "completed",
                this.props.isNonInstaller ? getUserId() : undefined,
                !this.props.isNonInstaller ? getUserId() : undefined,
                (progress, fileBeingUploaded) => {
                    const uploadStatus: FileUploadStatus = {
                        file: fileBeingUploaded,
                        progress,
                        status: progress === 100 ? "completed" : "uploading",
                    };
                    this.setState((prevState) => ({
                        onFinishedUploadProgresses: prevState.onFinishedUploadProgresses.length
                            ? prevState.onFinishedUploadProgresses
                                  .map((status) =>
                                      status.file.name === fileBeingUploaded.name ? uploadStatus : status,
                                  )
                                  .filter((status) => status.progress !== 100)
                            : [uploadStatus].filter((status) => status.progress !== 100),
                    }));
                },
            ),
        );
        Promise.all(fileUploadPromises)
            .then((attachments) => {
                this.setState({
                    alertContent: {
                        title: "Success",
                        message: "File(s) uploaded successfully",
                        severity: "success",
                    },
                    onFinishedAttachments: [
                        ...this.state.onFinishedAttachments,
                        ...(attachments.filter((a) => a !== null) as Attachment[]),
                    ],
                });
            })
            .catch((error) => {
                this.setState({
                    alertContent: {
                        title: "Error",
                        message: error.message,
                        severity: "error",
                    },
                });
            });
    };

    private onSubmitQuestion = (question: InstallerJobQuestionnaire) => {
        const { jobId } = this.props;
        jobService.submitQuestionnaire(jobId, question).then((updatedQuestion) => {
            if (!updatedQuestion) {
                return;
            }
            const existingQuestions = this.state.questions.concat([]);
            for (let i = 0; i < existingQuestions.length; i++) {
                if (existingQuestions[i].id === updatedQuestion.id) {
                    // merge the updated question with the existing one
                    existingQuestions[i] = { ...existingQuestions[i], ...updatedQuestion };
                    break;
                }
                if (existingQuestions[i].question === updatedQuestion.question) {
                    existingQuestions[i] = { ...existingQuestions[i], ...updatedQuestion };
                    break;
                }
            }
            this.setState({ questions: existingQuestions });
        });
    };
}

export const InstallerJobPage = withInstallerAuthentication(withParams(InstallerPageComponent));
